import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  startGoogleInstall,
  listGoogleConnections,
  disconnectGoogleConnection,
} from "@/lib/integrations/google.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/google")({ component: GoogleAdmin });

type Connection = {
  id: string;
  google_user_email: string;
  scopes: string | null;
  installed_at: string;
};

function GoogleAdmin() {
  const qc = useQueryClient();
  const fetchConns = useServerFn(listGoogleConnections);
  const installFn = useServerFn(startGoogleInstall);
  const disconnectFn = useServerFn(disconnectGoogleConnection);

  const { data, isLoading } = useQuery({
    queryKey: ["google-connections"],
    queryFn: () => fetchConns({}),
  });

  async function install() {
    try {
      const { authorize_url } = await installFn({ data: {} });
      window.location.href = authorize_url;
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const connections = (data?.connections ?? []) as Connection[];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Google</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Connect a Google account so CairnOS can deliver scheduled reports straight
            into a Google Sheet you own.
          </p>
        </div>
        <Button onClick={install}>Connect Google account</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Connected Accounts</CardTitle>
          <CardDescription>
            One row per Google account this tenant has authorized. Disconnecting
            here will stop any report subscriptions wired to that account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : connections.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No connections yet. Click <em>Connect Google account</em> to authorize one.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Connected</TableHead>
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.google_user_email}</TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate max-w-[24rem]">
                      {c.scopes ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(c.installed_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Disconnect"
                        onClick={async () => {
                          if (!confirm(`Disconnect ${c.google_user_email}?`)) return;
                          try {
                            await disconnectFn({ data: { connection_id: c.id } });
                            qc.invalidateQueries({ queryKey: ["google-connections"] });
                            toast.success("Disconnected");
                          } catch (e) {
                            toast.error((e as Error).message);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}